/**
 * VM-WORKSPACE-1 / VIEW-MODE-CLUSTER-RECONCILIATION-1 — Workspace schema.
 *
 * Zod v4 schema for the `workspaces` Firestore collection. Workspaces are
 * composable mission views — Bloomberg Launchpad metaphor for logistics —
 * that power View Mode B and feed AEGIS-composed workspaces in View Mode C.
 *
 * Tenant isolation: every read/write goes through DataService and carries
 * `tenantId`. Soft-delete only — `softDeleted: true` rather than removal.
 *
 * Schema versioning: `schemaVersion: 1` initial. Additive changes use
 * `.optional()` to remain backward compatible (architecture rule).
 */
import { z } from 'zod';

export const WORKSPACE_SHARING_SCOPES = Object.freeze(['private', 'team', 'tenant']);
export const WorkspaceSharingScopeSchema = z.enum(WORKSPACE_SHARING_SCOPES);

export const WORKSPACE_SOURCE_KINDS = Object.freeze([
  'manual',     // user-created
  'template',   // role-template-instantiated (VM-WORKSPACE-2)
  'aegis',      // AEGIS-composed (VM-WORKSPACE-3)
]);
export const WorkspaceSourceKindSchema = z.enum(WORKSPACE_SOURCE_KINDS);

/**
 * Panel placement on the workspace grid. `col` and `row` are 1-indexed
 * (CSS grid). `colSpan` and `rowSpan` default to 1.
 */
export const PanelPlacementSchema = z.object({
  panelType:    z.string().min(1),  // matches a key in panelRegistry
  panelConfig:  z.record(z.string(), z.any()).optional(), // panel-specific config
  col:          z.number().int().min(1),
  row:          z.number().int().min(1),
  colSpan:      z.number().int().min(1).default(1),
  rowSpan:      z.number().int().min(1).default(1),
  panelId:      z.string().min(1), // stable ID for drag/drop tracking
}).passthrough();

export const WorkspaceGridLayoutSchema = z.object({
  cols:         z.number().int().min(1).max(12).default(4),
  rows:         z.number().int().min(1).max(20).default(3),
}).passthrough();

export const WorkspaceSchema = z.object({
  id:           z.string().min(1),  // doc id == workspace id
  tenantId:     z.string().min(1),
  userId:       z.string().min(1),  // owner; sharingScope controls others' access
  name:         z.string().min(1).max(120),
  description:  z.string().max(500).optional(),
  panels:       z.array(PanelPlacementSchema).default([]),
  gridLayout:   WorkspaceGridLayoutSchema,
  autoRefreshIntervalMs: z.number().int().min(0).optional(), // 0 or absent = manual refresh
  sharingScope: WorkspaceSharingScopeSchema.default('private'),
  source:       WorkspaceSourceKindSchema.default('manual'),
  // VM-WORKSPACE-3 audit trail — populated when source === 'aegis'
  aegisIntent:  z.string().max(2000).optional(),
  aegisRationale: z.string().max(4000).optional(),
  // VM-WORKSPACE-2 — role template id when source === 'template'
  roleTemplateId: z.string().optional(),
  // Standard tenant-doc fields
  createdAt:    z.union([z.string(), z.object({})]).optional(),
  updatedAt:    z.union([z.string(), z.object({})]).optional(),
  softDeleted:  z.boolean().optional(),
  schemaVersion: z.number().int().default(1),
}).passthrough();

/** @typedef {import('zod').infer<typeof WorkspaceSchema>} Workspace */
/** @typedef {import('zod').infer<typeof PanelPlacementSchema>} PanelPlacement */

export const WORKSPACE_COLLECTION = 'workspaces';

// TMS workspace toggle — TMS-V2 (D-025 / DIFF-011): tms_workspace ∈
// {broker, shipper, carrier, both}. Consumed by src/stores/tmsWorkspaceStore.js
// (VALID_WORKSPACES = Object.values(TMS_WORKSPACE); default BROKER) and
// src/modules/tms screens. Restored — referenced by B1/B2 but never defined
// (TMS-TYPES-WORKSPACE-STOPSTATE-HOTFIX-1).
export const TMS_WORKSPACE = Object.freeze({
  BROKER:  'broker',
  SHIPPER: 'shipper',
  CARRIER: 'carrier',
  BOTH:    'both',
});

export const TmsWorkspaceEnum = z.enum(Object.values(TMS_WORKSPACE));
